/**
 * 주문 API 서비스
 *
 * @see olulo-mx-admin/app/Http/Controllers/Api/V1/OrderController.php
 */
import { apiClient, ApiClientError } from './client';
import { currentComensalHeaders, currentComensalOrderBlockReason } from '@/lib/comensalAccess';
import type {
  CreateOrderRequest,
  CancelOrderRequest,
  GuestOrderLookupRequest,
  GuestOrderLookupResponse,
  OrderResponse,
  OrderListResponse,
  OrderStatusResponse,
} from './types';
import {
  parseCreateOrderResponse,
  parseActiveOrderBatchResponse,
  parseGuestOrderLookupResponse,
  parseOrderListResponse,
  parseOrderResponse,
} from './orderValidation';

export interface GetOrdersParams {
  status?: string;
  order_type?: 'pickup' | 'table';
  page?: number;
  per_page?: number;
  [key: string]: string | number | boolean | undefined;
}

export const orderApi = {
  /**
   * 주문 생성
   * POST /api/v1/orders
   *
   * 인증 사용자 또는 비회원 주문 가능
   * 비회원 주문 시 customer_phone, guest_password 필수
   */
  createOrder: async (data: CreateOrderRequest): Promise<OrderResponse> => {
    const blockReason = data.order_type === 'table'
      ? currentComensalOrderBlockReason({ forceTableOrder: true })
      : null;
    if (blockReason !== null) {
      throw new ApiClientError(
        blockReason === 'split_disabled_with_ledger'
          ? 'Split payment is paused. Ask a staff member for help.'
          : 'Join the table before ordering.',
        422,
        blockReason === 'split_disabled_with_ledger'
          ? 'SPLIT_LEDGER_READ_ONLY'
          : 'COMENSAL_JOIN_REQUIRED',
      );
    }

    const headers = data.order_type === 'table' ? currentComensalHeaders() : undefined;
    const response = headers === undefined
      ? await apiClient.post<unknown>('/api/v1/orders', data)
      : await apiClient.post<unknown>('/api/v1/orders', data, { headers });

    return parseCreateOrderResponse(response, 'createOrder');
  },

  /**
   * 주문 상세 조회
   * GET /api/v1/orders/:orderId
   *
   * 인증 사용자 본인 주문만 조회 가능
   * 비회원 주문은 guestLookup 사용
   */
  getOrder: async (orderId: string | number): Promise<OrderResponse> =>
    parseOrderResponse(
      await apiClient.get<unknown>(`/api/v1/orders/${orderId}`),
      'getOrder'
    ),

  /**
   * 주문 목록 조회
   * GET /api/v1/orders
   *
   * 인증 필수 - 본인 주문만 조회
   */
  getOrders: async (params?: GetOrdersParams): Promise<OrderListResponse> =>
    parseOrderListResponse(
      await apiClient.get<unknown>('/api/v1/orders', { params }),
      'getOrders'
    ),

  /** Versioned, customer-scoped active buckets; old-server fallback belongs to the read adapter. */
  getActiveOrdersBatch: async () =>
    parseActiveOrderBatchResponse(
      await apiClient.get<unknown>('/api/v1/orders/active-batch')
    ),

  /**
   * 주문 취소
   * POST /api/v1/orders/:orderId/cancel
   *
   * - 인증 사용자: 본인 주문만 취소 가능
   * - 비회원: guest_password 필수
   * - pending 상태 주문만 취소 가능
   */
  cancelOrder: async (
    orderId: string | number,
    data?: CancelOrderRequest
  ): Promise<OrderResponse> =>
    parseOrderResponse(
      await apiClient.post<unknown>(`/api/v1/orders/${orderId}/cancel`, data),
      'cancelOrder'
    ),

  /**
   * 비회원 주문 조회
   * POST /api/v1/orders/guest/lookup
   *
   * 전화번호와 비밀번호로 최근 30일 주문 조회
   * Rate Limit: 분당 10회
   */
  guestLookup: async (data: GuestOrderLookupRequest): Promise<GuestOrderLookupResponse> =>
    parseGuestOrderLookupResponse(
      await apiClient.post<unknown>('/api/v1/orders/guest/lookup', data, { authFailureScope: 'resource' }),
      'guestLookup'
    ),

  /**
   * 주문 상태 폴링
   * POST /api/v1/orders/:orderId/status
   *
   * 주문 상태만 빠르게 조회 (Long-polling 지원)
   * 비회원 주문: guest_password 필수
   */
  pollStatus: (orderId: string | number, guestPassword?: string) =>
    apiClient.post<OrderStatusResponse>(`/api/v1/orders/${orderId}/status`, {
      guest_password: guestPassword,
    }),

  /**
   * 비회원 주문을 회원 계정으로 병합
   * POST /api/v1/orders/guest/merge
   *
   * 로그인한 사용자만 호출 가능
   * 전화번호와 비밀번호로 비회원 주문을 인증 후 현재 계정에 병합
   *
   * @param data.phone - 비회원 주문에 사용된 전화번호
   * @param data.password - 비회원 주문 비밀번호
   * @returns 병합된 주문 수 및 ID 목록
   */
  mergeGuestOrders: (data: { phone: string; password: string }) =>
    apiClient.post<{
      message: string;
      merged_count: number;
      merged_order_ids: number[];
    }>('/api/v1/orders/guest/merge', data),

  // 결제 API는 별도의 paymentApi로 분리되었습니다.
  // @see /lib/api/payments.ts
};
